<template>
    <div
        v-if="store.state.isMaintenanceMode"
        class="maintenance"
    >
        <img
            class="fixed top-0 left-0 opacity-5 z-0"
            alt="logo"
            src="./assets/svg/login-left.svg"
        />
        <img
            class="fixed bottom-0 right-0 opacity-5 z-0"
            alt="logo"
            src="./assets/svg/login-right.svg"
        />
        <div class="content">
            <img
                :src="logo"
                alt="Clere Logo"
                class="clere-logo"
            />
            <p class="maintenance-text">
                Our website is temporarily offline for scheduled maintenance. We
                will be back soon.
            </p>
            <p class="maintenance-text">Thank you for understanding.</p>
        </div>
    </div>
    <div
        v-else
        id="app"
        class="max-h-screen overflow-y-hidden overflow-x-hidden"
    >
        <div
            :key="`notification-banner-${notificationBannerLocal.isActive}`"
            v-if="
                notificationBannerLocal &&
                notificationBannerLocal.isActive &&
                !isBannerDismissed
            "
            class="relative w-full z-[999999] text-center shadow-lg flex-shrink-0"
            :style="{
                backgroundColor: notificationBannerLocal.backgroundColor,
                color: notificationBannerLocal.textColor,
            }"
        >
            <div class="px-6 py-3 font-medium text-center">
                <p>
                    <span>{{ notificationBannerLocal.title }}: </span>
                    <span v-html="notificationBannerLocal.content"></span>
                </p>
            </div>

            <button
                type="button"
                aria-label="Dismiss notification"
                class="absolute right-3 top-1/2 -translate-y-1/2"
                @click="dismissBanner"
            >
                <i class="pi pi-times"></i>
            </button>
        </div>

        <component
            class="flex flex-col w-full h-full"
            :class="{ 'overflow-y-auto': false }"
            :is="currentTheme"
        >
            <!-- START NEW LAYOUT -->
            <div class="relative flex flex-1 w-full h-full">
                <NavigationDrawer
                    class="z-50"
                    v-if="store.getters.isAuthenticated && !isOnboardingView"
                />
                <div class="overflow-y-auto relative w-full h-full flex-1">
                    <div
                        class="relative w-full h-full flex flex-col z-40"
                        :class="{
                            'container mx-auto px-2 sm:px-8':
                                !store.getters.isFullScreen,
                        }"
                    >
                        <div class="flex-1">
                            <AppBar
                                class="z-50"
                                :class="{
                                    'sticky top-0': [
                                        'pinzone-editor',
                                        'pinzone-editor-id',
                                    ].includes($route.name),
                                }"
                                v-if="
                                    store.getters.isAuthenticated &&
                                    !isPublicView
                                "
                            />
                            <RouterView class="relative w-full h-full z-40" />
                        </div>
                        <Footer
                            v-if="
                                !store.getters.isFullScreen &&
                                store.getters.isAuthenticated
                            "
                        />
                    </div>
                </div>
            </div>
            <!-- END NEW LAYOUT -->
        </component>

        <div id="dialog-target" />

        <notifications
            classes="clere-notification"
            :speed="300"
            :duration="8000"
            position="bottom right"
            style="z-index: 99999999999"
            group="default"
        />

        <notifications
            classes="clere-notification"
            :speed="300"
            :duration="1000"
            :max="1"
            position="top center"
            :style="{
                pointerEvents: 'none',
                zIndex: '99999999999',
                top: store.state.isMobile
                    ? '65px'
                    : 'calc(10px + var(--navbar-height))',
                width: store.state.isMobile ? '60%' : 'auto',
                left: store.state.isMobile ? 'calc(50% - 125px)' : 'auto',
            }"
            group="fullscreen"
        />

        <notifications
            classes="clere-notification"
            :speed="300"
            :duration="20000"
            :max="1"
            position="bottom center"
            style="z-index: 99999999999"
            group="reset-password"
        />
    </div>
</template>

<script lang="ts">
// vue
import {
    defineComponent,
    onMounted,
    onBeforeMount,
    computed,
    watch,
    ref,
} from '@vue/composition-api';

// hooks
import { useStore } from '@/store';
import logo from '@/assets/clereplan/thumbnail_ClerePlan_logo_negative.png';

import { notificationBanner } from '@/vue3/composables/useBannerNotification';

// components
import AppBar from '@/vue3/components/AppBar.vue';
import Footer from '@/vue3/components/Footer.vue';
import NavigationDrawer from '@/vue3/components/NavigationDrawer.vue';

import { useService } from '@/vue3/api/services';

export default defineComponent({
    components: {
        AppBar,
        Footer,
        NavigationDrawer,
    },
    setup(props: any, vm) {
        // hooks
        const store = useStore();
        const { getNotificationBanner } = useService();

        // properties
        const mobileWidth = 1024;

        // reactive
        const isUtilityOpen = ref(false);

        const temporaryBanner = {
            title: 'Introducing the Unified Tool 🎉',
            content:
                'We’ve combined the Translate, Modify Geometry, and Rotate tools into one streamlined Unified Tool. Please <a href="https://clere-golf.b-cdn.net/tutorial_videos/new/UnifiedEditNew.mp4" target="_blank" rel="noopener noreferrer" style="text-decoration: underline; color: inherit; cursor: pointer;">click here to watch the video tutorial</a>.',
            backgroundColor: '#78B3F0',
            textColor: '#e6e5e2',
            isActive: true,
        };

        const notificationBannerLocal = ref(temporaryBanner);

        const isBannerDismissed = ref(
            localStorage.getItem('drawing-tool-banner-dismissed') === 'true',
        );

        const dismissBanner = () => {
            isBannerDismissed.value = true;
            localStorage.setItem('drawing-tool-banner-dismissed', 'true');
        };

        // computed
        const currentTheme = computed(() => {
            return () => import('./vue3/components/themes/VelaBlue.vue');
        });

        const isPublicView = computed(() => {
            const route = vm.root.$route;
            return route.path.includes('/public/course');
        });

        const isOnboardingView = computed(() => {
            return vm.root.$route.name === 'course-setup';
        });

        // methods
        const setIsUtilityOpen = (value: boolean) => {
            isUtilityOpen.value = value;
        };

        // watch
        watch(
            () => vm.root.$route.name,
            (state: any) => {
                store.commit(
                    'setIsFullScreen',
                    store.state.fullScreenRoutes.indexOf(state) !== -1,
                );
                notificationBannerLocal.value = temporaryBanner;
            },
            { immediate: true },
        );

        // lifecycle

        onMounted(() => {
            if (window.innerWidth >= mobileWidth) {
                store.state.isMobile = false;
            } else {
                store.state.isMobile = true;
            }

            window.addEventListener('resize', () => {
                if (window.innerWidth >= mobileWidth) {
                    store.state.isMobile = false;
                } else {
                    store.state.isMobile = true;
                }
            });
        });

        return {
            logo,
            store,
            isUtilityOpen,
            notificationBannerLocal,
            isPublicView,
            isOnboardingView,
            setIsUtilityOpen,
            currentTheme,
            isBannerDismissed,
            dismissBanner,
        };
    },
});
</script>
<style scoped>
/* Ensure full height for body and root */
html,
body {
    height: 100%;
    margin: 0;
    padding: 0;
}

/* Main container styling */
.maintenance {
    display: flex;
    justify-content: center; /* Center content vertically */
    align-items: center; /* Center content horizontally */
    height: 100vh; /* Full viewport height */
    margin: 0;
    text-align: center;
}

/* Content container for stacking image and text */
.content {
    display: flex;
    flex-direction: column; /* Stack items vertically */
    align-items: center; /* Center items horizontally */
}

/* Logo styling */
.clere-logo {
    width: 200px; /* Adjust size as needed */
    height: auto;
    margin-bottom: 20px; /* Space below the logo */
}

/* Text styling */
.maintenance-text {
    font-size: 16px; /* Adjust font size as needed */
    color: rgb(234, 230, 230); /* Neutral color for readability */
    margin: 0;
    padding: 0 10px; /* Optional padding for spacing */
}
.navigation-bar {
    display: none;
}
#course-dropdown {
    display: none;
}
</style>
